import React, { useState } from 'react';
import { Modal, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import AsyncSelect from 'react-select/async';
import { useAuthContext } from '../../context/AuthContext';
import locationApi from '../../api/locations/locationApi';
import eventApi from '../../api/events/eventApi';
import { STORAGE_KEYS } from '../../api/config';

const CreateEventModal = ({ show, handleClose }) => {
  const { user, events, fetchProfile } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [eventTypes, setEventTypes] = useState([
    { id: 1, name: 'Harusi', value: 'Harusi' },
    { id: 2, name: 'Kitchen-party', value: 'Kitchen-party' },
    { id: 3, name: 'Send-off', value: 'Send-off' },
    { id: 4, name: 'Engagement', value: 'Engagement' },
  ]);

  React.useEffect(() => {
    if (show) {
      fetchEventTypes();
    }
  }, [show]);

  const fetchEventTypes = async () => {
    try {
      const response = await eventApi.getEventTypes();
      const payload = response?.data?.data || response?.data || response;
      const list = Array.isArray(payload) ? payload : (payload?.event_types || payload?.types || []);
      if (Array.isArray(list) && list.length > 0) {
        const formatted = list.map((item, idx) => {
          if (typeof item === 'string') {
            return { id: idx, name: item, value: item };
          }
          return {
            id: item.id || idx,
            name: item.name || item.title || item.event_type || item.type || String(item),
            value: item.event_type || item.type || item.name || item.title || String(item)
          };
        });
        setEventTypes(formatted);
      }
    } catch (err) {
      console.warn('Could not fetch dynamic event types, using fallback:', err?.message);
    }
  };
  
  const [formData, setFormData] = useState({
    eventType: '',
    eventDate: '',
    eventRegion: '',
    eventTitle: '',
    eventDescription: '',
    mobile: '',
    acceptTerms: true
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    setError('');
  };

  const loadLocationOptions = async (inputValue) => {
    if (inputValue.length < 3) {
      return [];
    }
    const results = await locationApi.searchLocations(inputValue);
    return results.map(loc => ({
      value: loc.id || loc.location_id || loc.name,
      label: loc.name || loc.title || loc.location_name || String(loc),
      id: loc.id || loc.location_id
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.eventType) {
      setError('Tafadhali chagua aina ya shughuli');
      return;
    }

    if (!formData.eventDate) {
      setError('Tafadhali chagua tarehe ya shughuli');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const userName = user?.name || user?.user_name || 'User';
      const locationId = formData.location_id || 1;

      const eventData = {
        event_title: formData.eventTitle || `${userName}'s ${formData.eventType}`,
        event_description: formData.eventDescription || `${formData.eventType} ya ${userName}`,
        event_type: formData.eventType,
        mobile: formData.mobile,
        event_date: formData.eventDate,
        location_id: locationId,
        event_location: formData.eventRegion || 'Tanzania'
      };

      const response = await eventApi.registerEvent(eventData);

      if (response.success || response.status === 800 || response.data) {
        toast.success('Shughuli mpya imetengenezwa kikamilifu!');

        try {
          // Fetch the updated events list
          const eventsResponse = await eventApi.getUserEvents();
          const updatedEvents = eventsResponse?.data?.data || eventsResponse?.data || [];

          if (Array.isArray(updatedEvents) && updatedEvents.length > 0) {
            // Update user events in localStorage
            const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
            if (storedUser) {
              const userData = JSON.parse(storedUser);
              userData.events = updatedEvents;
              localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
            }

            // Find the newly created event (the one not present in current events list)
            const oldEvents = events || [];
            const newEvent = updatedEvents.find(e => !oldEvents.some(old => old.id === e.id)) || updatedEvents[0];

            if (newEvent) {
              localStorage.setItem('selectedEventId', newEvent.id?.toString());
              localStorage.setItem(STORAGE_KEYS.UID, newEvent.event_address);
              localStorage.setItem(STORAGE_KEYS.EVENT_ID, newEvent.id);
            }
          }
        } catch (fetchErr) {
          console.error('Error fetching updated events:', fetchErr);
        }

        // Refresh the user profile to fetch the updated events list
        await fetchProfile();
        
        handleClose();
        
        // Reload to update the context state and show the new event in the dropdown
        window.location.reload();
      } else {
        setError(response.message || 'Hitilafu katika kusajili shughuli mpya');
      }
    } catch (err) {
      setError(err.message || 'Hitilafu katika kusajili. Tafadhali jaribu tena.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Tengeneza Shughuli Mpya</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-4">Jaza taarifa zinazo hitajika kuhusu shughuli yako mpya</p>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="formBasicEventTitle">
            <Form.Label>Jina la Shughuli (Optional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Mfano: Harusi ya Juma na Asha"
              name="eventTitle"
              value={formData.eventTitle}
              onChange={handleInputChange}
            />
            <Form.Text className="text-muted">
              Ukiacha, tutatumia jina lako na aina ya shughuli
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formBasicEventType">
            <Form.Label>Aina ya Shughuli *</Form.Label>
            <Form.Select
              name="eventType"
              value={formData.eventType}
              onChange={handleInputChange}
              required
            >
              <option value="">Chagua...</option>
              {Array.isArray(eventTypes) && eventTypes.map((type, idx) => (
                <option key={type.id || idx} value={type.value || type.name}>
                  {type.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formBasicMobile">
            <Form.Label>Namba ya Simu ya Shughuli *</Form.Label>
            <Form.Control
              type="text"
              name="mobile"
              value={formData.mobile}
              onChange={handleInputChange}
              required
              placeholder="Mfano: 0717392442"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formBasicEventDate">
            <Form.Label>Tarehe ya shughuli *</Form.Label>
            <Form.Control
              type="date"
              name="eventDate"
              value={formData.eventDate}
              onChange={handleInputChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formBasicEventRegion">
            <Form.Label>Mkoa / Mahali (Optional)</Form.Label>
            <AsyncSelect
              cacheOptions
              defaultOptions={false}
              loadOptions={loadLocationOptions}
              placeholder="Mfano: Arusha , Dar es salaam"
              noOptionsMessage={({ inputValue }) =>
                inputValue.length < 3 ? "Andika walau herufi 3 kutafuta" : "Hakuna mahali palipopatikana"
              }
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  eventRegion: selectedOption ? selectedOption.label : '',
                  location_id: selectedOption ? (selectedOption.id || selectedOption.value) : null
                });
              }}
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#4169e1' : '#ced4da',
                  borderRadius: '0.375rem',
                  padding: '2px'
                })
              }}
            />
          </Form.Group>

          <div className="d-flex justify-content-end mt-4">
            <Button variant="light" className="me-2" onClick={handleClose} disabled={loading}>
              Funga
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : 'Tengeneza Shughuli'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateEventModal;
